import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { list as listCompetitionClubs } from '../../../../lib/api/competitionClubs';
import {
  editCompetitionRound,
  listRoundTypes
} from '../../../../lib/api/match';

const getClubEntityId = item => {
  return (
    item?.entities_id ??
    item?.club_entities_id ??
    item?.clubs_entities_id ??
    item?.bye_club_entities_id ??
    null
  );
};

export function EditRound({ competitions, initialData, onClose, onSave }) {
  const [roundTypes, setRoundTypes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedRoundType, setSelectedRoundType] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [loadingRoundTypes, setLoadingRoundTypes] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const competitionId = initialData?.competitions_id;

  const selectedCompetition = useMemo(
    () =>
      competitions.find(
        item => String(item.competitions_id) === String(competitionId)
      ) || null,
    [competitionId, competitions]
  );

  const competitionOption = useMemo(() => {
    if (!selectedCompetition) return null;

    return {
      value: selectedCompetition.competitions_id,
      label: selectedCompetition.name
    };
  }, [selectedCompetition]);

  useEffect(() => {
    const fetchDependencies = async () => {
      if (!selectedCompetition) {
        setRoundTypes([]);
        setClubs([]);
        setSelectedRoundType(null);
        setSelectedClub(null);
        return;
      }

      setLoadingRoundTypes(true);
      setLoadingClubs(true);

      try {
        const [roundTypeResponse, clubResponse] = await Promise.all([
          listRoundTypes({
            competitions_type_id: selectedCompetition.competitions_type_id
          }),
          listCompetitionClubs(selectedCompetition.competitions_guid)
        ]);

        const nextRoundTypes = (roundTypeResponse || []).map(item => ({
          value: item.match_round_type_id,
          label: item.name,
          raw: item
        }));

        const nextClubs = (clubResponse || [])
          .map(item => ({
            value: getClubEntityId(item),
            label: item.clubs_name || item.name,
            raw: item
          }))
          .filter(item => item.value !== null);

        setRoundTypes(nextRoundTypes);
        setClubs(nextClubs);

        setSelectedRoundType(
          nextRoundTypes.find(
            item =>
              Number(item.value) === Number(initialData?.match_round_type_id)
          ) || null
        );

        setSelectedClub(
          nextClubs.find(
            item =>
              Number(item.value) === Number(initialData?.bye_club_entities_id)
          ) || null
        );
      } catch (error) {
        console.error(error);
        toast.error('Doslo je do greske prilikom ucitavanja sifarnika.');
        setRoundTypes([]);
        setClubs([]);
      } finally {
        setLoadingRoundTypes(false);
        setLoadingClubs(false);
      }
    };

    fetchDependencies();
  }, [initialData, selectedCompetition]);

  const handleSubmit = async event => {
    event.preventDefault();

    if (!initialData?.competition_round_guid || !selectedRoundType) {
      toast.error('Kolo je obavezno.');
      return;
    }

    setIsSubmitting(true);
    try {
      await editCompetitionRound(initialData.competition_round_guid, {
        match_round_type_id: Number(selectedRoundType.value),
        bye_club_entities_id: selectedClub ? Number(selectedClub.value) : null
      });

      toast.success('Kolo je uspjesno izmijenjeno.');
      onSave?.(competitionId);
      onClose?.();
    } catch (error) {
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Doslo je do greske prilikom cuvanja.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="competitions_id">
              <Form.Label>Takmičenje</Form.Label>
              <Select
                options={competitionOption ? [competitionOption] : []}
                value={competitionOption}
                classNamePrefix="react-select"
                isDisabled
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="match_round_type_id">
              <Form.Label>Kolo</Form.Label>
              <Select
                options={roundTypes}
                value={selectedRoundType}
                classNamePrefix="react-select"
                placeholder="Pretraži kolo..."
                onChange={option => setSelectedRoundType(option || null)}
                isDisabled={loadingRoundTypes || isSubmitting}
                isLoading={loadingRoundTypes}
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} md="12" controlId="bye_club_entities_id">
              <Form.Label>Slobodan klub</Form.Label>
              <Select
                options={clubs}
                value={selectedClub}
                classNamePrefix="react-select"
                placeholder="Pretraži klub..."
                onChange={option => setSelectedClub(option || null)}
                isDisabled={loadingClubs || isSubmitting}
                isLoading={loadingClubs}
                isClearable
              />
            </Form.Group>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Zatvori
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Sačuvaj...
                </>
              ) : (
                'Sačuvaj'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

EditRound.propTypes = {
  competitions: PropTypes.array.isRequired,
  initialData: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

export default EditRound;
